<template>
  <div class="user-review-queue">
    <AppSectionHero
      :title="$t('pages.user.reviewQueue.title')"
      :subtitle="$t('pages.user.reviewQueue.subtitle')"
      :stats="heroStats"
      :show-refresh="false"
    />

    <CCard class="border-0 shadow-sm shell">
      <CCardBody class="p-4">
        <div class="queue-toolbar toolbar">
          <div class="search-box">
            <span class="search-icon">🔍</span>
            <input v-model="searchQuery" class="queue-search-input" :placeholder="text.searchPlaceholder" :aria-label="text.searchPlaceholder">
          </div>

          <select v-model="activeStatus" class="queue-filter filter-select" :aria-label="text.filterStatus">
            <option value="all">{{ text.allStatuses }}</option>
            <option value="pending">{{ text.stats.pending }}</option>
            <option value="rejected">{{ text.stats.rejected }}</option>
            <option value="approved">{{ text.stats.approved }}</option>
          </select>
        </div>


        <div v-if="filteredQueueItems.length" class="table-shell">
          <div class="table-head">
            <div class="table-head__copy">{{ showingLabel(filteredQueueItems.length, text.queueItemsLabel) }}</div>
          </div>
          <div class="queue-table">
            <div class="queue-table__header">
              <div>{{ text.columns.agreement }}</div>
              <div>{{ text.columns.type }}</div>
              <div>{{ text.columns.partner }}</div>
              <div>{{ text.columns.status }}</div>
              <div>{{ text.columns.actions }}</div>
            </div>
            <div
              v-for="item in filteredQueueItems"
              :key="item.id"
              class="queue-table__row"
            >
              <div class="queue-table__cell">
                <div class="queue-title">{{ item.title }}</div>
                <div v-if="latestComment(item)" class="queue-note">{{ latestComment(item) }}</div>
              </div>
              <div class="queue-table__cell">
                <span class="queue-value">{{ item.activityType }}</span>
              </div>
              <div class="queue-table__cell">
                <span class="queue-value">{{ item.partner }}</span>
              </div>
              <div class="queue-table__cell">
                <span class="status-pill" :class="statusPillClass(item)">{{ displayStageLabel(item) }}</span>
              </div>
              <div class="queue-table__cell queue-table__cell--actions">
                <button
                  class="secondary-btn queue-action-btn"
                  :title="actionLabel(item)"
                  :aria-label="actionLabel(item)"
                  @click="openItem(item)"
                >
                  <CIcon :name="opensEditableReview(item) ? 'cil-pencil' : 'cil-folder-open'" />
                </button>
              </div>

            </div>
          </div>
        </div>
        <div v-else class="empty-state">{{ text.emptyQueue }}</div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import Service from '@/service/api'
import AppSectionHero from '@/projects/components/layout/AppSectionHero'
import { canAutoRefresh } from '@/projects/utils/refresh-guard'
import { clearPageCache, readPageCache, writePageCache } from '@/utils/page-cache'
import {
  getAgreementWorkflowStatusLabel,
  getAgreementWorkflowStatusTone
} from '@/utils/agreement-workflow-status'

const USER_REVIEW_QUEUE_CACHE_KEY = 'user-review-queue-page'
const USER_REVIEW_QUEUE_CACHE_MAX_AGE = 5 * 60 * 1000
const PAGE_TEXT = {
  en: {
    pendingSection: 'In Review',
    rejectedSection: 'Rejected Agreements',
    approvedSection: 'Approved Agreements',
    queueItemsLabel: 'review items',
    emptyQueue: 'No review items match this status.',
    pendingItemsLabel: 'in review items',
    rejectedItemsLabel: 'rejected items',
    approvedItemsLabel: 'approved items',
    emptyPending: 'No pending review items right now.',
    emptyRejected: 'No rejected agreements right now.',
    emptyApproved: 'No approved agreements yet.',
    columns: {
      agreement: 'Agreement',
      type: 'Type',
      partner: 'Partner',
      status: 'Status',
      actions: 'Actions'
    },
    stats: {
      pending: 'In Review',
      rejected: 'Rejected',
      approved: 'Approved'
    },
    reviewSteps: 'Review Steps',
    view: 'View',
    inReview: 'In Review',
    draft: 'Draft',
    update: 'Update',
    general: 'General',
    viewSummary: 'View summary',
    filterStatus: 'Filter status',
    allStatuses: 'All Status'
  },
  th: {
    pendingSection: 'อยู่ระหว่างการพิจารณา',
    rejectedSection: 'ข้อตกลงที่ถูกส่งกลับแก้ไข',
    approvedSection: 'ข้อตกลงที่อนุมัติแล้ว',
    queueItemsLabel: 'รายการตรวจทาน',
    emptyQueue: 'ไม่พบรายการตรวจทานในสถานะนี้',
    pendingItemsLabel: 'รายการที่อยู่ระหว่างการพิจารณา',
    rejectedItemsLabel: 'รายการที่ถูกส่งกลับแก้ไข',
    approvedItemsLabel: 'รายการที่อนุมัติแล้ว',
    emptyPending: 'ขณะนี้ยังไม่มีรายการที่อยู่ระหว่างการพิจารณา',
    emptyRejected: 'ขณะนี้ยังไม่มีข้อตกลงที่ถูกส่งกลับแก้ไข',
    emptyApproved: 'ยังไม่มีข้อตกลงที่อนุมัติแล้ว',
    columns: {
      agreement: 'ข้อตกลง',
      type: 'ประเภท',
      partner: 'คู่สัญญา',
      status: 'สถานะ',
      actions: 'การดำเนินการ'
    },
    stats: {
      pending: 'กำลังพิจารณา',
      rejected: 'ส่งกลับแก้ไข',
      approved: 'อนุมัติแล้ว'
    },
    reviewSteps: 'ขั้นตอนการตรวจทาน',
    view: 'ดู',
    inReview: 'อยู่ระหว่างการพิจารณา',
    draft: 'ฉบับร่าง',
    update: 'อัปเดต',
    general: 'ทั่วไป',
    viewSummary: 'ดูสรุป',
    filterStatus: 'กรองสถานะ',
    allStatuses: 'ทุกสถานะ'
  }
}

export default {
  name: 'UserReviewQueue',
  components: { AppSectionHero },
  data() {
    return {
      pending: [],
      history: [],
      activeStatus: 'all',
      searchQuery: '',
      queueLoading: false
    }
  },
  computed: {
    localeCode() {
      return String(this.$i18n && this.$i18n.locale ? this.$i18n.locale : 'en').toLowerCase().startsWith('th') ? 'th' : 'en'
    },
    text() {
      return PAGE_TEXT[this.localeCode]
    },
    shouldForceRefresh() {
      return !!(this.$route && this.$route.query && this.$route.query.refresh)
    },
    rejected() {
      return this.pending.filter(item => item.derivedStage === 'revision')
    },
    activePending() {
      return this.pending.filter(item => item.derivedStage !== 'revision')
    },
    allQueueItems() {
      return [...this.activePending, ...this.rejected, ...this.history]
    },
    filteredQueueItems() {
      const base = (() => {
        if (this.activeStatus === 'pending') return this.activePending
        if (this.activeStatus === 'rejected') return this.rejected
        if (this.activeStatus === 'approved') return this.history
        return this.allQueueItems
      })()

      const query = String(this.searchQuery || '').trim().toLowerCase()
      if (!query) return base

      return base.filter((item) => {
        const haystack = [
          item && item.title,
          item && item.partner,
          item && item.activityType,
          item && item.derivedStageLabel,
          item && item.currentStage
        ]
          .filter(Boolean)
          .join(' / ')
          .toLowerCase()

        return haystack.includes(query)
      })
    },
    heroStats() {
      return [
        {
          label: this.text.stats.pending,
          key: 'pending',
          value: this.activePending.length,
          icon: 'cil-clock',
          iconClass: 'app-section-stat__icon--attention'
        },
        {
          label: this.text.stats.rejected,
          value: this.rejected.length,
          icon: 'cil-x-circle',
          iconClass: 'app-section-stat__icon--danger'
        },
        {
          label: this.text.stats.approved,
          value: this.history.length,
          icon: 'cil-check-circle',
          iconClass: 'app-section-stat__icon--active'
        }
      ]
    }
  },
  mounted() {
    if (this.shouldForceRefresh) {
      clearPageCache(USER_REVIEW_QUEUE_CACHE_KEY)
    } else {
      this.loadCachedQueue()
    }
    this.loadQueue(this.shouldForceRefresh)
    this.refreshTimer = window.setInterval(() => {
      if (canAutoRefresh()) this.loadQueue(true)
    }, 60000)
    window.addEventListener('focus', this.handleWindowFocus)
  },
  beforeDestroy() {
    if (this.refreshTimer) window.clearInterval(this.refreshTimer)
    window.removeEventListener('focus', this.handleWindowFocus)
  },
  methods: {
    showingLabel(count, label) {
      return `Showing ${count} ${label}`
    },
    loadCachedQueue() {
      const cached = readPageCache(USER_REVIEW_QUEUE_CACHE_KEY, USER_REVIEW_QUEUE_CACHE_MAX_AGE)
      if (!cached || typeof cached !== 'object') return
      this.pending = Array.isArray(cached.pending) ? cached.pending : []
      this.history = Array.isArray(cached.history) ? cached.history : []
    },
    handleWindowFocus() {
      if (canAutoRefresh()) this.loadQueue(true)
    },
    async loadQueue(forceRefresh = false) {
      if (this.queueLoading) return
      this.queueLoading = true
      try {
        const response = await Service.agreements('review-queue', {}, { forceRefresh })
        const data = response && response.data && response.data.data ? response.data.data : {}
        this.pending = data.pending || []
        this.history = data.history || []
        writePageCache(USER_REVIEW_QUEUE_CACHE_KEY, {
          pending: this.pending,
          history: this.history
        })
        if (forceRefresh && this.shouldForceRefresh) {
          this.$router.replace({ path: this.$route.path, query: {} }).catch(() => {})
        }
      } catch (error) {
        if (!this.pending.length && !this.history.length) {
          this.pending = []
          this.history = []
        }
      } finally {
        this.queueLoading = false
      }
    },
    opensEditableReview(item) {
      const stage = String(item && item.derivedStage || '').trim().toLowerCase()
      return ['submitted', 'suggestion', 'revision'].includes(stage)
    },
    openItem(item) {
      if (this.opensEditableReview(item)) {
        this.$router.push(`/dashboard/user-agreements/${item.id}/start-review`)
        return
      }
      this.viewSummary(item.id)
    },
    actionLabel(item) {
      return this.opensEditableReview(item)
        ? this.text.reviewSteps
        : this.text.view
    },
    statusPillClass(item) {
      return `status-pill--${getAgreementWorkflowStatusTone(item)}`
    },
    displayStageLabel(item) {
      return getAgreementWorkflowStatusLabel(item)
    },
    viewSummary(id) {
      this.$router.push(`/dashboard/user-agreements/${id}/review-summary?mode=view`)
    },
    latestComment(item) {
      const comments = item && item.workflow && Array.isArray(item.workflow.comments) ? item.workflow.comments : []
      if (!comments.length) return ''
      const latest = comments[comments.length - 1]
      return `${latest.roleLabel || latest.role || this.text.update}: ${latest.articleTitle || latest.section || this.text.general}`
    }
  }
}
</script>

<style scoped lang="scss">
@import "../shared/workspace-consistency.scss";

@include workspace-page-frame("user-review-queue");
@include workspace-toolbar-search;
@include workspace-button-system;

.shell { border-radius: 20px; }
.queue-meta, .empty-state, .queue-note, .table-head__copy { color: #718096; }
.queue-title { color: #344767; font-weight: 700; }
.history-block { margin-top: 24px; }
.section-title { color: #344767; margin: 0 0 12px; }
.queue-toolbar {
  align-items: center;
  display: flex;
  justify-content: flex-end;
  margin-bottom: 22px;
}
.queue-filter {
  min-width: 0;
  width: 220px;
}
.table-shell {
  border-radius: 20px;
}
.table-head {
  margin-bottom: 14px;
}
.table-head__copy {
  display: block;
  margin-top: 6px;
  padding-left: 18px;
}
.queue-table {
  background: #fff;
  border: 1px solid #e6ebf1;
  border-radius: 18px;
  overflow: hidden;
}
.queue-table__header,
.queue-table__row {
  align-items: center;
  column-gap: 20px;
  display: grid;
  grid-template-columns: minmax(220px, 1.45fr) minmax(120px, 0.85fr) minmax(170px, 1fr) minmax(130px, 0.8fr) 76px;
  padding: 18px 20px;
}
.queue-table__header {
  background: #f8fafc;
  border-bottom: 1px solid #e6ebf1;
  color: #52606d;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.queue-table__row {
  border-bottom: 1px solid #eef1f6;
}
.queue-table__row:last-child {
  border-bottom: 0;
}
.queue-table__cell {
  min-width: 0;
}
.queue-table__cell--actions {
  display: flex;
  justify-content: center;
}
.queue-value {
  color: #344767;
  line-height: 1.5;
}
.status-pill {
  border-radius: 999px;
  display: inline-flex;
  font-size: 0.8rem;
  font-weight: 700;
  justify-content: center;
  min-width: 112px;
  padding: 8px 12px;
  text-align: center;
}
.status-pill--draft {
  background: #e2e3e5;
  color: #383d41;
}
.status-pill--pending {
  background: #fff3cd;
  color: #856404;
}
.status-pill--approved {
  background: #dcfce7;
  color: #166534;
}
.status-pill--admin {
  background: #e0f2fe;
  color: #2563eb;
}
.status-pill--rejected,
.status-pill--revision {
  background: #ffe5e5;
  color: #b91c1c;
}
.queue-action-btn {
  font-size: 0.95rem;
  min-height: 38px;
  min-width: 38px;
  padding: 0;
}
@media (max-width: 768px) {
  .queue-toolbar {
    justify-content: stretch;
  }

  .queue-filter {
    width: 100%;
  }

  .queue-table__header {
    display: none;
  }

  .queue-table__row {
    align-items: flex-start;
    grid-template-columns: 1fr;
    row-gap: 10px;
  }

  .queue-table__cell--actions {
    justify-content: flex-start;
  }
}
</style>
